/* eslint-disable no-unused-vars */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { Stack, ToggleButton, Toolbar, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { format } from 'date-fns';
import { YEAR_MONTH_DAY_PATTERN } from 'modules/common/constants/date-range';
import { withTheme } from '@emotion/react';
import { TABLE_HEADER_FONT_COLOR } from 'modules/common/constants/style';
import { selectAlertData, selectSubscriptionData } from '../selectors';
import {
  formatLabel,
  getAlertFrequency,
  isSemanticType,
  normalizeSemanticAlertName,
} from '../functions';
import {
  selectHotelDropdownList,
  selectHotelName,
  selectIsDemoModeActivated,
} from '../../../../selectors';
import { alertActions } from '../slice';
import { selectAuthUser, selectOrganizationId } from '../../../../../common/auth/selectors';
import { ALERT_TYPES, AUDIT_TABLE_PAGE_SIZE, SEMANTIC_NAME_GROUPED_PROC } from '../constants';
import { DrawerTableStyles } from '../style';

/**
 * Alert list handled by this component
 * @param {*} isShow - set show/hide state of side drawer
 * @param {*} setSelectedAlertId - selected alert id
 * @returns
 */
const AlertTableView = ({ isShow, setSelectedAlertId, setIsEditable, isDrawerOpen = false }) => {
  const dispatch = useDispatch();
  const userAlerts = useSelector(selectAlertData);
  const hotelList = useSelector(selectHotelDropdownList);
  const subscriptionData = useSelector(selectSubscriptionData);
  const organizationId = useSelector(selectOrganizationId);
  const currentUser = useSelector(selectAuthUser);
  const selectedHotel = useSelector(selectHotelName);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  //
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const enrichAlertsWithSubscription = (alertsByType) =>
    alertsByType.map((alertGroup) => {
      const subsForGroup = subscriptionData?.filter((sub) =>
        alertGroup.alertIds.includes(sub.alertId)
      );
      let isNotify = false;
      if (subsForGroup && subsForGroup.length > 0) {
        isNotify = subsForGroup.some((s) => s.toNotify === true);
      } else {
        const originalAlerts = userAlerts?.results.filter((a) =>
          alertGroup.alertIds.includes(a.id)
        );
        isNotify = originalAlerts?.some((a) => a.toNotify === true);
      }
      //
      return {
        ...alertGroup,
        toNotify: isNotify,
        channel: isNotify ? ['EMAIL'] : [],
      };
    });

  // Table columns
  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'auditName',
      headerName: 'Name',
      headerClassName: 'super-app-theme--header',
      flex: isDrawerOpen ? 2.5 : 1,
      minWidth: isDrawerOpen ? 200 : 350,
    },
    {
      field: 'type',
      headerName: 'Type',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      minWidth: isDrawerOpen ? 120 : 200,
    },
    {
      field: 'hotels',
      headerName: 'Hotel(s)',
      headerClassName: 'super-app-theme--header',
      flex: isDrawerOpen ? 1 : 2,
      minWidth: isDrawerOpen ? 110 : 250,
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      headerClassName: 'super-app-theme--header',
      flex: 1,
      minWidth: isDrawerOpen ? 100 : 180,
    },
    {
      field: 'channel',
      headerName: 'Notification Channels',
      headerClassName: 'super-app-theme--header',
      flex: isDrawerOpen ? 1.5 : 1,
      minWidth: isDrawerOpen ? 190 : 280,
      renderCell: (params) => {
        const { alertIds, toNotify, type } = params.row;
        const handleChange = () => {
          dispatch(
            alertActions.updateUserSubscriptions({
              organizationId,
              userId: currentUser?.id,
              alertIds,
              toNotify: !toNotify,
              notificationType: 'EMAIL',
              isSemanticAlert: isSemanticType(type),
            })
          );
        };
        //
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              '& .MuiToggleButton-root': {
                borderRadius: '999px',
                height: 30,
                px: 1.5,
                gap: 0.75,
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1,
                textTransform: 'none',
                borderColor: '#e4e7eb',
                backgroundColor: '#f7f8fa',
                color: 'text.secondary',
                '& .MuiSvgIcon-root': { fontSize: 14 },
                '&:hover': {
                  backgroundColor: '#eef1f5',
                },
                '&.Mui-selected': {
                  backgroundColor: '#356ddd',
                  borderColor: '#356ddd',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: '#2d60c7',
                    borderColor: '#2d60c7',
                  },
                },
                '&.Mui-disabled': {
                  backgroundColor: '#f7f8fa',
                  borderColor: '#e4e7eb',
                  color: '#a1a7b0',
                },
              },
            }}
          >
            <ToggleButton
              value="EMAIL"
              size="small"
              selected={toNotify}
              onChange={handleChange}
              sx={{ minWidth: 85 }}
              aria-label={`E-mail notifications for ${params.row.auditName}`}
              tabIndex={params.hasFocus ? 0 : -1}
            >
              <MailOutlineIcon />
              E-mail
            </ToggleButton>
            <ToggleButton value="TEXT" size="small" sx={{ minWidth: 72 }} disabled>
              <SmsOutlinedIcon sx={{ transform: 'translateY(1px)' }} />
              Text
            </ToggleButton>
          </Stack>
        );
      },
    },
  ];
  //
  const columnsWithTooltip = columns.map((col) => ({
    ...col,
    description: {
      auditName:
        "An 'alert' has been set up to evaluate hotel performance metrics and highlight future trends or exceptions.",
      type:
        'Pace-in-Brief: Compares the hotel’s future periods performance to past periods.\n' +
        'Horizon Briefing: Identifies future stay dates with notable performance changes for the hotel(s).',
      channel:
        'The delivery method used to notify you when alert thresholds are triggered. Turning off a channel will stop notifications from being sent via that delivery method.',
      frequency: 'The frequency in which this alert evaluates hotel performance metrics',
    }[col.field],
    renderHeader: (params) => (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {params.colDef.headerName}
        </Typography>

        {params.colDef.description && (
          <Tooltip title={params.colDef.description} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer' }} />
          </Tooltip>
        )}
      </Stack>
    ),
  }));

  // group alerts & enrich with subscription data
  useEffect(() => {
    if (!userAlerts?.results) return;
    const sortedAlerts = [...userAlerts.results].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    // Group alerts by type
    const groupedAlerts = sortedAlerts.reduce((acc, alert) => {
      const { typeName, auditName } = ALERT_TYPES[alert.alertProc];

      // HACK (temporary): semantic report variants share one alertProc, so group those
      // rows additionally by the normalized report name to allow toggling each report
      // variant's email separately. Parsing the name is fragile; a proper reportType
      // field / distinct alertProc per variant should replace this in the refactor.
      const isNameGrouped = SEMANTIC_NAME_GROUPED_PROC.includes(alert.alertProc);
      const reportName = isNameGrouped
        ? normalizeSemanticAlertName(alert.name, alert.hotelIds)
        : null;

      const key = isNameGrouped ? `${typeName}::${reportName}` : typeName;

      if (!acc[key]) {
        let hotelsForAlert;
        if (alert.alertProc === 'semantic-analysis-trend') {
          hotelsForAlert = isDemoModeActivated ? selectedHotel?.altName : selectedHotel?.name;
        } else {
          hotelsForAlert = hotelList
            ?.filter((hotel) => alert?.hotelIds?.includes(hotel?.hotelCode))
            ?.sort((a, b) => {
              const nameA = (isDemoModeActivated ? a.altName : a.name) ?? '';
              const nameB = (isDemoModeActivated ? b.altName : b.name) ?? '';
              return nameA.localeCompare(nameB, undefined, {
                numeric: true,
                sensitivity: 'base',
              });
            })
            ?.map((hotel) => (isDemoModeActivated ? hotel.altName : hotel.name))
            ?.join(', ');
        }
        // Hide audits if user doesn't have access to any hotels
        if (!hotelsForAlert) {
          return acc;
        }
        //
        acc[key] = {
          id: key,
          type: typeName,
          // Semantic variants display their friendly report name instead of the
          // generic proc-level description (part of the same hack noted above).
          // Grouping still uses the normalized
          // name so each report variant keeps its own row
          auditName: reportName ? alert.name || reportName : auditName,
          alertIds: [],
          hotels: hotelsForAlert,
          channel: [],
          // TODO (revisit): this group merges multiple alerts that share a type;
          // alerts are sorted by createdAt desc, so the first alert seen is the
          // latest and its frequency is displayed assuming all grouped alerts have
          // the same schedule configuration. Revisit if grouped alerts can ever
          // diverge in evalLead/targetDateRecurrence.
          frequency: getAlertFrequency(alert),
        };
      }
      //
      acc[key].alertIds.push(alert.id);
      return acc;
    }, {});
    //
    const alertsByType = Object.values(groupedAlerts);
    const enrichedAlerts = enrichAlertsWithSubscription(alertsByType);
    setFilteredAlerts(enrichedAlerts);
  }, [userAlerts, subscriptionData, hotelList, selectedHotel]);

  // set selected alert base on row
  const onSelectedRowChange = (ids, isEditable) => {
    if (ids?.length) {
      setSelectedAlertId(ids[0]);
      isShow(true);
      setIsEditable(isEditable);
    }
  };
  // define table column visibility modal
  const columnVisibilityModel = {
    id: false,
  };
  //
  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} mb={2} mt={8}>
        <Typography variant="h4">Configuration</Typography>
        <Tooltip
          title="Automated alerts that evaluate hotel performance data and indicate future performance."
          arrow
          placement="top"
        >
          <InfoOutlinedIcon
            sx={{
              fontSize: 18,
              color: 'text.secondary',
              cursor: 'pointer',
              mt: '2px',
            }}
          />
        </Tooltip>
      </Stack>
      <div style={{ width: '100%', minWidth: 0 }}>
        <DataGrid
          autoHeight
          sx={{
            '& .super-app-theme--header': {
              backgroundColor: 'white',
              color: TABLE_HEADER_FONT_COLOR,
            },
            '& .MuiDataGrid-main': {
              backgroundColor: 'white',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: 'white',
            },
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '6px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '10px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '20px' },
            ...(isDrawerOpen ? DrawerTableStyles : {}),
          }}
          columnVisibilityModel={columnVisibilityModel}
          rowHeight={50}
          rowsPerPageOptions={[5, 10, 15]}
          getRowHeight={() => 'auto'}
          rows={filteredAlerts ?? []}
          columns={columnsWithTooltip}
          // onSelectionModelChange={(ids) => onSelectedRowChange(ids, true)}
          initialState={{
            pagination: { pageSize: AUDIT_TABLE_PAGE_SIZE },
            sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
          }}
          hideFooterSelectedRowCount
        />
      </div>
    </>
  );
};
//
export default withTheme(AlertTableView);
